import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import MasterLocationSelect from "components/MasterLocationSelect";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";
import SoftSnackbar from "components/SoftSnackbar";
import SoftTypography from "components/SoftTypography";
import { API_URL } from "config";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useModulePermissions } from "utils/modulePermissions";
import { useAuth } from "../../AuthContext";
import { invoiceActionPermission, invoiceFormFieldPermission } from "./invoicePermissions";
import { clientInvoiceAmounts } from "./clientInvoiceAmounts";

const emptyAddress = { name: "", email: "", address: "", city: "", state: "", postal_code: "", country: "" };
const addressFields = [
  ["name", "Name / Company"],
  ["email", "Email"],
  ["address", "Address"],
  ["city", "City / Location"],
  ["state", "State"],
  ["postal_code", "Pincode"],
  ["country", "Country"],
];
const requiredCreateFields = [
  "project_codes",
  "client",
  "invoice_date",
  "shipping_same_as_billing",
  "items_section",
  "item_project_code",
  "item_country",
  "item_quantity",
  "item_rate",
  "item_amount",
  "billing_currency",
  "exchange_rate",
  "tax",
];
const createItemFields = [
  "item_project_code",
  "item_country",
  "item_description",
  "item_quantity",
  "item_rate",
  "item_amount",
  "add_remove_items",
];
const defaultCurrencyOptions = [
  { value: "USD", label: "USD", code: "USD", symbol: "$" },
  { value: "INR", label: "INR", code: "INR", symbol: "₹" },
  { value: "GBP", label: "GBP", code: "GBP", symbol: "£" },
  { value: "EURO", label: "EURO", code: "EURO", symbol: "€" },
  { value: "AED", label: "AED", code: "AED", symbol: "د.إ" },
  { value: "SGD", label: "SGD", code: "SGD", symbol: "S$" },
];

const mergeCurrencyOptions = (apiOptions = []) => {
  const apiByCode = new Map((apiOptions || []).map((option) => {
    const code = String(option?.code || option?.value || "").trim().toUpperCase();
    return [code, option];
  }));

  return defaultCurrencyOptions.map((fallback) => {
    const apiOption = apiByCode.get(fallback.code) || {};
    return {
      value: fallback.code,
      label: fallback.code,
      code: fallback.code,
      symbol: apiOption.symbol || fallback.symbol,
    };
  });
};

const localToday = () => {
  const date = new Date();
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const money = (value) => Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const decimalStatus = (value, label, maxIntegerDigits = null) => {
  const text = String(value ?? "").trim();
  if (!text) return { valid: false, message: `${label} is required. Use format 100.00.` };
  if (!/^\d*(?:\.\d*)?$/.test(text) || !/^\d/.test(text)) {
    return { valid: false, message: `${label} can contain only digits and one decimal point. Use format 100.00.` };
  }
  const [integerPart, fractionPart] = text.split(".");
  if (maxIntegerDigits && integerPart.length > maxIntegerDigits) {
    return { valid: false, message: `${label} allows maximum ${maxIntegerDigits} digits before the decimal point. Use format 100.00.` };
  }
  if (fractionPart === undefined || fractionPart.length !== 2) {
    return { valid: false, message: `${label} must contain exactly 2 digits after the decimal point. Use format 100.00.` };
  }
  return { valid: true, message: `Valid format: ${text}` };
};

const exchangeRateValidation = (value) => {
  const text = String(value ?? "").trim();
  if (!/^\d{1,3}(?:\.\d{1,7})?$/.test(text)) {
    return { valid: false, message: "Exchange rate must have up to 3 digits before and up to 7 digits after the decimal point." };
  }
  if (!(Number(text) > 0)) {
    return { valid: false, message: "Exchange rate must be greater than zero." };
  }
  return { valid: true, message: `Valid exchange rate: ${text}` };
};
const normalizeDecimalInput = (value) => {
  if (value === "") return "";
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(2) : value;
};

const normalizeExchangeRateInput = (value) => String(value ?? "").trim();

const currencyDisplay = (value, currency) => {
  const code = String(currency?.code || "USD").toUpperCase();
  const symbols = { USD: "$", INR: "₹", GBP: "£", EURO: "€", EUR: "€", AED: "د.إ", SGD: "S$" };
  const symbol = symbols[code] || (currency?.symbol && currency.symbol !== code ? currency.symbol : code);
  return `${symbol}${money(value)} ${code}`;
};

const projectInvoiceCellSx = {
  px: 0.45,
  py: 0.55,
  fontSize: "11px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  "& .MuiInputBase-root": {
    width: "100%",
    minHeight: "34px",
    height: "34px",
    fontSize: "12px",
  },
};

const projectInvoiceHeaderSx = {
  px: 0.55,
  py: 0.7,
  fontSize: "11px",
  lineHeight: 1.2,
  whiteSpace: "normal",
};

function InvoiceCreate() {
  const navigate = useNavigate();
  const { permissions = [], userPermission = [] } = useAuth();
  const { hasModulePermission } = useModulePermissions(
    permissions,
    userPermission,
    "invoices.client"
  );
  const [clients, setClients] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [minimumDate, setMinimumDate] = useState("");
  const [maximumDate, setMaximumDate] = useState(localToday());
  const [form, setForm] = useState({
    client_id: null,
    project_ids: [],
    reference_number: "",
    invoice_date: localToday(),
    exchange_rate: "",
    tax_enabled: true,
    shipping_same_as_billing: true,
    billing: { ...emptyAddress },
    shipping: { ...emptyAddress },
  });
  const [company, setCompany] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [currencyOptions, setCurrencyOptions] = useState(defaultCurrencyOptions);
  const [taxRule, setTaxRule] = useState({ applicable: false, percentage: 0, rule: "", label: "" });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partyLoading, setPartyLoading] = useState(false);
  const [lineLoading, setLineLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });
  const [exchangeRateStatus, setExchangeRateStatus] = useState(null);
  const [serverVisibility, setServerVisibility] = useState({});
  const canCreateField = (field) => {
    const permissionField = field.replace(/^(billing|shipping)_city$/, "$1_address");
    return hasModulePermission(invoiceFormFieldPermission("client", "create", permissionField))
      && serverVisibility[permissionField] !== false;
  };
  const canReturnToList = hasModulePermission("invoices.client.pages.list.view");
  const canGenerateInvoice = hasModulePermission("invoices.client.pages.create.view")
    && hasModulePermission(invoiceActionPermission("client", "create"))
    && requiredCreateFields.every(canCreateField);
  const visibleCreateItemColumnCount = createItemFields.filter(canCreateField).length;

  const requestHeaders = useMemo(() => ({
    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
    Accept: "application/json",
  }), []);

  useEffect(() => {
    const controller = new AbortController();
    const loadOptions = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(`${API_URL}/invoices/client/form-options?mode=create`, {
          headers: requestHeaders,
          signal: controller.signal,
        });
        const result = await response.json();
        if (!response.ok || !result.success) throw new Error(result.message || "Unable to load invoice options.");
        setClients(result.data?.clients || []);
        setAllProjects(result.data?.projects || []);
        setCurrencyOptions(mergeCurrencyOptions(result.data?.currencies));
        setMinimumDate(result.data?.minimum_invoice_date || "");
        setMaximumDate(result.data?.maximum_invoice_date || result.data?.today || localToday());
        setServerVisibility(result.data?.visibility || result.data?.form_visibility || {});
        setForm((current) => ({ ...current, invoice_date: result.data?.today || current.invoice_date }));
      } catch (requestError) {
        if (requestError.name !== "AbortError") setError(requestError.message || "Unable to load invoice options.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    loadOptions();
    return () => controller.abort();
  }, [requestHeaders]);

  const projectOptions = useMemo(() => (
    form.client_id
      ? allProjects.filter((option) => Number(option.client_id) === Number(form.client_id))
      : allProjects
  ), [allProjects, form.client_id]);
  const selectedClient = clients.find((option) => Number(option.value) === Number(form.client_id)) || null;
  const selectedProjects = form.project_ids
    .map((projectId) => allProjects.find((option) => Number(option.value) === Number(projectId)))
    .filter(Boolean);
  const totalCompletes = useMemo(
    () => items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0),
    [items]
  );
  const sourceAmountUsd = useMemo(
    () => items.reduce((sum, item) => sum + (Number(item.total) || 0), 0),
    [items]
  );
  const totals = useMemo(
    () => clientInvoiceAmounts(sourceAmountUsd, form.exchange_rate, form.tax_enabled, taxRule, currency?.code || "USD"),
    [currency?.code, form.exchange_rate, form.tax_enabled, sourceAmountUsd, taxRule]
  );

  const resetParty = () => {
    setCompany(null);
    setCurrency(null);
    setTaxRule({ applicable: false, percentage: 0, rule: "", label: "" });
    setItems([]);
    setForm((current) => ({
      ...current,
      client_id: null,
      project_ids: [],
      exchange_rate: "",
      billing: { ...emptyAddress },
      shipping: current.shipping_same_as_billing ? { ...emptyAddress } : current.shipping,
    }));
  };

  const loadPartyDetails = async (clientId) => {
    setPartyLoading(true);
    try {
      const response = await fetch(`${API_URL}/invoices/client/party/${clientId}/form-details?mode=create`, {
        headers: requestHeaders,
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || "Unable to load client details.");
      const billing = { ...emptyAddress, ...(result.data?.billing || {}) };
      const invoicingCompany = result.data?.invoicing_company || null;
      setCompany(invoicingCompany);
      const rawCurrency = result.data?.currency || null;
      const rawCurrencyCode = String(rawCurrency?.code || "USD").toUpperCase();
      const fallbackCurrency = currencyOptions.find((option) => option.code === rawCurrencyCode)
        || defaultCurrencyOptions.find((option) => option.code === rawCurrencyCode)
        || defaultCurrencyOptions[0];
      const nextCurrency = rawCurrency ? {
        ...rawCurrency,
        value: rawCurrencyCode,
        label: rawCurrencyCode,
        code: rawCurrencyCode,
        symbol: rawCurrency.symbol || fallbackCurrency.symbol,
      } : null;
      const isUsd = String(nextCurrency?.code || "USD").toUpperCase() === "USD";
      setCurrency(nextCurrency);
      setTaxRule(result.data?.tax || { applicable: false, percentage: 0, rule: "", label: "" });
      setForm((current) => ({
        ...current,
        client_id: clientId,
        billing,
        exchange_rate: isUsd ? "1.00" : "",
        tax_enabled: true,
        shipping: current.shipping_same_as_billing ? { ...billing } : current.shipping,
      }));
      setExchangeRateStatus(isUsd ? exchangeRateValidation("1.00") : null);
      if (result.data?.company_warning) setError(result.data.company_warning);
    } finally {
      setPartyLoading(false);
    }
  };

  const loadProjectLines = async (clientId, projectIds) => {
    setLineLoading(true);
    try {
      const response = await fetch(`${API_URL}/invoices/client/project-lines?mode=create`, {
        method: "POST",
        headers: { ...requestHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ client_id: clientId, project_ids: projectIds }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        const validationMessage = result.errors ? Object.values(result.errors).flat()[0] : null;
        throw new Error(validationMessage || result.message || "Unable to load project invoice rows.");
      }
      setItems((result.data?.lines || []).map((item) => ({
        ...item,
        description: item.description || "",
        rate: item.rate === "" || item.rate === null || item.rate === undefined ? "" : normalizeDecimalInput(item.rate),
        total: item.total === "" || item.total === null || item.total === undefined ? "" : normalizeDecimalInput(item.total),
      })));
    } finally {
      setLineLoading(false);
    }
  };

  const chooseProjects = async (options) => {
    const nextOptions = options || [];
    if (nextOptions.length === 0) {
      resetParty();
      setError("");
      return;
    }

    const clientIds = [...new Set(nextOptions.map((option) => Number(option.client_id)).filter(Boolean))];
    if (clientIds.length !== 1 || (form.client_id && Number(form.client_id) !== clientIds[0])) {
      const message = "Client mismatch with previous one.";
      setSnackbar({ open: true, color: "error", message });
      setError(message);
      return;
    }

    const clientId = clientIds[0];
    const projectIds = nextOptions.map((option) => Number(option.value));
    setError("");
    setForm((current) => ({ ...current, client_id: clientId, project_ids: projectIds }));
    setItems([]);
    try {
      if (!form.client_id) await loadPartyDetails(clientId);
      await loadProjectLines(clientId, projectIds);
    } catch (requestError) {
      const message = requestError.message || "Unable to load selected project details.";
      setError(message);
      setSnackbar({ open: true, color: "error", message });
    }
  };

  const setItem = (index, key, value) => {
    setItems((current) => current.map((item, itemIndex) => {
      if (itemIndex !== index) return item;
      const next = { ...item, [key]: value };
      if (key === "quantity" || key === "rate") {
        next.total = ((Number(next.quantity) || 0) * (Number(next.rate) || 0)).toFixed(2);
      }
      return next;
    }));
  };

  const showDecimalFeedback = (value, label, maxIntegerDigits = null) => {
    const status = decimalStatus(value, label, maxIntegerDigits);
    setSnackbar({ open: true, message: status.message, color: status.valid ? "success" : "error" });
    return status;
  };

  const validateExchangeRate = (value) => {
    const status = exchangeRateValidation(value);
    setExchangeRateStatus(status);
    return status;
  };

  const selectCurrency = (option) => {
    const nextCurrency = option ? {
      ...option,
      code: String(option.code || option.value || "USD").toUpperCase(),
    } : null;
    const nextCode = nextCurrency?.code || "USD";
    setCurrency(nextCurrency);
    setForm((current) => ({
      ...current,
      exchange_rate: nextCode === "USD" && (!current.exchange_rate || Number(current.exchange_rate) === 0)
        ? "1.00"
        : current.exchange_rate,
    }));
  };

  const addBlankRowAfter = (index) => {
    const blankRow = {
      project_id: null,
      project_detail_id: null,
      project_code: "",
      country: "",
      description: "",
      quantity: "",
      rate: "",
      total: "",
    };
    setItems((current) => [
      ...current.slice(0, index + 1),
      blankRow,
      ...current.slice(index + 1),
    ]);
  };

  const toggleSameAsBilling = (checked) => {
    setForm((current) => ({
      ...current,
      shipping_same_as_billing: checked,
      shipping: checked ? { ...current.billing } : current.shipping,
    }));
  };

  const generateInvoice = async () => {
    if (!form.client_id || form.project_ids.length === 0) {
      setError("Please select at least one eligible project code.");
      return;
    }
    if (!(Number(form.exchange_rate) > 0)) {
      const message = "Exchange rate is required and must be greater than zero. Use format 100.00.";
      setError(message);
      setExchangeRateStatus({ valid: false, message });
      return;
    }
    const exchangeValidation = validateExchangeRate(form.exchange_rate);
    if (!exchangeValidation.valid) {
      setError(exchangeValidation.message);
      setSnackbar({ open: true, message: exchangeValidation.message, color: "error" });
      return;
    }
    const invalidItem = items.find((item) => (
      !decimalStatus(item.rate, "Client CPI").valid || !decimalStatus(item.total, "Amount").valid
    ));
    if (invalidItem) {
      const message = "Every Client CPI and Amount must use exactly 2 decimal places, for example 100.00.";
      setError(message);
      setSnackbar({ open: true, message, color: "error" });
      return;
    }

    setSaving(true);
    setError("");
    try {
      const payload = {};
      if (canCreateField("client")) payload.client_id = form.client_id;
      if (canCreateField("project_codes")) payload.project_ids = form.project_ids;
      if (canCreateField("client_po")) payload.reference_number = form.reference_number || null;
      if (canCreateField("invoice_date")) payload.invoice_date = form.invoice_date;
      if (canCreateField("shipping_same_as_billing")) {
        payload.shipping_same_as_billing = form.shipping_same_as_billing;
      }
      addressFields.forEach(([key]) => {
        if (canCreateField(`shipping_${key}`)) {
          payload[`shipping_${key}`] = form.shipping[key] || null;
        }
      });
      if (canCreateField('shipping_country')) {
        payload.shipping_country_id = form.shipping.country_id || null;
      }
      if (canCreateField('shipping_state')) {
        payload.shipping_state_id = form.shipping.state_id || null;
      }
      if (canCreateField("billing_currency")) {
        payload.currency_code = currency?.code || "USD";
        payload.currency_symbol = currency?.symbol || null;
      }
      if (canCreateField("exchange_rate")) payload.exchange_rate = Number(form.exchange_rate);
      if (canCreateField("tax")) payload.tax_enabled = Boolean(form.tax_enabled);
      if (canCreateField("items_section")) {
        payload.items = items.map((item) => {
          const invoiceItem = {};
          if (canCreateField("item_project_code")) {
            invoiceItem.project_id = item.project_id || null;
            invoiceItem.project_detail_id = item.project_detail_id || null;
            invoiceItem.project_code = item.project_code;
          }
          if (canCreateField("item_country")) invoiceItem.country = item.country;
          if (canCreateField("item_description")) invoiceItem.description = item.description || null;
          if (canCreateField("item_quantity")) invoiceItem.quantity = Number(item.quantity || 0);
          if (canCreateField("item_rate")) invoiceItem.rate = Number(item.rate || 0);
          if (canCreateField("item_amount")) invoiceItem.total = Number(item.total || 0);
          return invoiceItem;
        });
      }
      const response = await fetch(`${API_URL}/invoices/client`, {
        method: "POST",
        headers: { ...requestHeaders, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        const validationMessage = result.errors ? Object.values(result.errors).flat()[0] : null;
        throw new Error(validationMessage || result.message || "Unable to generate invoice.");
      }
      navigate("/invoices/client", {
        replace: true,
        state: {
          invoiceSuccessMessage: result.message || `Invoice ${result.invoice_number} generated successfully.`,
        },
      });
    } catch (requestError) {
      const message = requestError.message || "Unable to generate invoice.";
      setError(message);
      setSnackbar({ open: true, color: "error", message });
    } finally {
      setSaving(false);
    }
  };

  const setShippingField = (key, value) => {
    setForm((current) => ({ ...current, shipping: { ...current.shipping, [key]: value } }));
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={2}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <SoftTypography variant="h4" fontWeight="medium">Generate Client Invoice</SoftTypography>
          {canReturnToList && <SoftButton color="secondary" size="small" onClick={() => navigate("/invoices/client")}>
            <Icon>arrow_back</Icon>&nbsp; Invoices
          </SoftButton>}
        </SoftBox>

        {loading ? (
          <SoftBox py={8} display="flex" justifyContent="center"><CircularProgress color="info" /></SoftBox>
        ) : (
          <Card sx={{ p: { xs: 1.5, md: 2.5 } }}>
            {(canCreateField("project_codes") || canCreateField("client_po")) && <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "3fr 1fr" }} gap={1.25} mb={1.5}>
              {canCreateField("project_codes") && <SoftBox>
                <SoftTypography variant="caption">Project Codes * (multiple allowed)</SoftTypography>
                <SoftSelect
                  placeholder="Select closed projects"
                  options={projectOptions}
                  value={selectedProjects}
                  isMulti
                  isSearchable
                  isLoading={lineLoading || partyLoading}
                  onChange={chooseProjects}
                />
                {form.client_id && projectOptions.length === form.project_ids.length && (
                  <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
                    No other eligible project code is available for this client.
                  </SoftTypography>
                )}
              </SoftBox>}
              {canCreateField("client_po") && <SoftBox>
                <SoftTypography variant="caption">Client PO</SoftTypography>
                <SoftInput value={form.reference_number} placeholder="Enter client PO" onChange={(event) => setForm((current) => ({ ...current, reference_number: event.target.value }))} />
              </SoftBox>}
            </SoftBox>}

            {(canCreateField("client") || canCreateField("invoice_date") || canCreateField("invoice_month")) && <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "2fr 1fr 1fr" }} gap={1.25} mb={1.5}>
              {canCreateField("client") && <SoftBox>
                <SoftTypography variant="caption">Client (auto-selected)</SoftTypography>
                <SoftSelect placeholder="Selected from project code" options={clients} value={selectedClient} isDisabled isLoading={partyLoading} />
              </SoftBox>}
              {canCreateField("invoice_date") && <SoftBox>
                <SoftTypography variant="caption">Invoice Date *</SoftTypography>
                <SoftInput type="date" value={form.invoice_date} inputProps={{ min: minimumDate, max: maximumDate }} onChange={(event) => setForm((current) => ({ ...current, invoice_date: event.target.value }))} />
              </SoftBox>}
              {canCreateField("invoice_month") && <SoftBox>
                <SoftTypography variant="caption">Invoice Year-Month</SoftTypography>
                <SoftInput value={(form.invoice_date || "").slice(0, 7)} disabled />
              </SoftBox>}
            </SoftBox>}

            {((company && canCreateField("invoicing_company")) || (currency && canCreateField("billing_currency"))) && (
              <SoftBox display="flex" flexWrap="wrap" gap={2} mb={2} p={1.25} sx={{ backgroundColor: "#f8fafc", borderRadius: "8px" }}>
                {canCreateField("invoicing_company") && <SoftTypography variant="button"><strong>Invoicing Company:</strong> {company?.name || "-"} ({company?.country || "-"})</SoftTypography>}
                {canCreateField("billing_currency") && <SoftTypography variant="button"><strong>Client Billing Currency:</strong> {currency?.code || "USD"}</SoftTypography>}
              </SoftBox>
            )}

            <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2} mb={2}>
              {addressFields.some(([key]) => canCreateField(`billing_${key}`)) && <SoftBox>
                <SoftTypography variant="h6" mb={0.75}>Billing To Client</SoftTypography>
                {addressFields.filter(([key]) => !["state", "country"].includes(key) && canCreateField(`billing_${key}`)).map(([key, label]) => (
                  <SoftBox key={key} mb={0.7}><SoftTypography variant="caption">{label}</SoftTypography><SoftInput value={form.billing[key] || ""} disabled /></SoftBox>
                ))}
                <MasterLocationSelect
                  countryId={form.billing.country_id}
                  countryName={form.billing.country}
                  stateId={form.billing.state_id}
                  stateName={form.billing.state}
                  disabled
                  showCountry={canCreateField("billing_country")}
                  showState={canCreateField("billing_state")}
                  onCountryChange={() => {}}
                  onStateChange={() => {}}
                />
              </SoftBox>}
              {(canCreateField("shipping_same_as_billing") || addressFields.some(([key]) => canCreateField(`shipping_${key}`))) && <SoftBox>
                <SoftBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={0.5} mb={0.4}>
                  <SoftTypography variant="h6">Shipping To Client</SoftTypography>
                  {canCreateField("shipping_same_as_billing") && <FormControlLabel control={<Checkbox size="small" checked={form.shipping_same_as_billing} onChange={(event) => toggleSameAsBilling(event.target.checked)} />} label="Same as Billing" />}
                </SoftBox>
                {addressFields.filter(([key]) => !["state", "country"].includes(key) && canCreateField(`shipping_${key}`)).map(([key, label]) => (
                  <SoftBox key={key} mb={0.7}>
                    <SoftTypography variant="caption">{label}</SoftTypography>
                    <SoftInput value={form.shipping[key] || ""} disabled={form.shipping_same_as_billing} onChange={(event) => setShippingField(key, event.target.value)} />
                  </SoftBox>
                ))}
                <MasterLocationSelect
                  countryId={form.shipping.country_id}
                  countryName={form.shipping.country}
                  stateId={form.shipping.state_id}
                  stateName={form.shipping.state}
                  disabled={form.shipping_same_as_billing}
                  showCountry={canCreateField("shipping_country")}
                  showState={canCreateField("shipping_state")}
                  onCountryChange={({ id: countryId, name }) => setForm((current) => ({
                    ...current,
                    shipping: {
                      ...current.shipping,
                      country_id: countryId,
                      country: name,
                      state_id: null,
                      state: "",
                    },
                  }))}
                  onStateChange={({ id: stateId, name }) => setForm((current) => ({
                    ...current,
                    shipping: { ...current.shipping, state_id: stateId, state: name },
                  }))}
                />
              </SoftBox>}
            </SoftBox>

            {canCreateField("items_section") && <SoftBox mb={2}>
              <SoftTypography variant="h6" mb={0.75}>Project Country Invoice Rows</SoftTypography>
              {lineLoading ? (
                <SoftBox py={3} display="flex" justifyContent="center"><CircularProgress size={24} color="info" /></SoftBox>
              ) : (
                <TableContainer sx={{ border: "1px solid #e5e7eb", borderRadius: "8px", overflowX: "hidden", width: "100%" }}>
                  <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
                    <TableHead sx={{ display: "table-header-group" }}>
                      <TableRow>
                        {canCreateField("item_project_code") && <TableCell sx={projectInvoiceHeaderSx}>Project Code</TableCell>}
                        {canCreateField("item_country") && <TableCell sx={projectInvoiceHeaderSx}>Country</TableCell>}
                        {canCreateField("item_description") && <TableCell sx={projectInvoiceHeaderSx}>Description</TableCell>}
                        {canCreateField("item_quantity") && <TableCell sx={projectInvoiceHeaderSx}>Total Complete</TableCell>}
                        {canCreateField("item_rate") && <TableCell sx={projectInvoiceHeaderSx}>Client CPI (USD)</TableCell>}
                        {canCreateField("item_amount") && <TableCell sx={projectInvoiceHeaderSx}>Amount (USD)</TableCell>}
                        {canCreateField("add_remove_items") && <TableCell sx={projectInvoiceHeaderSx} align="center">Action</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((item, index) => (
                        <TableRow key={`${item.project_id || "project"}-${item.project_detail_id || "study"}-${index}`}>
                          {canCreateField("item_project_code") && <TableCell sx={projectInvoiceCellSx}><SoftInput value={item.project_code || ""} onChange={(event) => setItem(index, "project_code", event.target.value)} /></TableCell>}
                          {canCreateField("item_country") && <TableCell sx={projectInvoiceCellSx}><SoftInput value={item.country || ""} onChange={(event) => setItem(index, "country", event.target.value)} /></TableCell>}
                          {canCreateField("item_description") && <TableCell sx={projectInvoiceCellSx}><SoftInput value={item.description || ""} placeholder="Description" onChange={(event) => setItem(index, "description", event.target.value)} /></TableCell>}
                          {canCreateField("item_quantity") && <TableCell sx={projectInvoiceCellSx}><SoftInput type="number" value={item.quantity ?? 0} inputProps={{ min: 0, step: 1 }} onChange={(event) => setItem(index, "quantity", event.target.value)} /></TableCell>}
                          {canCreateField("item_rate") && <TableCell sx={projectInvoiceCellSx}>
                            <SoftBox>
                              <SoftInput type="text" value={item.rate ?? ""} inputProps={{ inputMode: "decimal" }} icon={{ component: "$", direction: "left" }} onChange={(event) => { const value = event.target.value; setItem(index, "rate", value); showDecimalFeedback(value, "Client CPI"); }} onBlur={() => { const value = normalizeDecimalInput(item.rate); setItem(index, "rate", value); showDecimalFeedback(value, "Client CPI"); }} />
                            </SoftBox>
                          </TableCell>}
                          {canCreateField("item_amount") && <TableCell sx={projectInvoiceCellSx}>
                            <SoftBox>
                              <SoftInput type="text" value={item.total ?? ""} inputProps={{ inputMode: "decimal" }} icon={{ component: "$", direction: "left" }} onChange={(event) => { const value = event.target.value; setItem(index, "total", value); showDecimalFeedback(value, "Amount"); }} onBlur={() => { const value = normalizeDecimalInput(item.total); setItem(index, "total", value); showDecimalFeedback(value, "Amount"); }} />
                            </SoftBox>
                          </TableCell>}
                          {canCreateField("add_remove_items") && <TableCell sx={{ ...projectInvoiceCellSx, px: 0.1 }} align="center">
                            <IconButton color="info" size="small" sx={{ p: 0.2 }} title="Add blank row below" onClick={() => addBlankRowAfter(index)}><Icon sx={{ fontSize: "18px !important" }}>add_circle</Icon></IconButton>
                            <IconButton color="error" size="small" sx={{ p: 0.2 }} title="Delete row" onClick={() => setItems((current) => current.filter((_, itemIndex) => itemIndex !== index))}><Icon sx={{ fontSize: "18px !important" }}>delete</Icon></IconButton>
                          </TableCell>}
                        </TableRow>
                      ))}
                      {items.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={Math.max(visibleCreateItemColumnCount, 1)} align="center">
                            Select project codes to load their country rows.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              <SoftBox display="flex" justifyContent="flex-end" flexWrap="wrap" gap={3} mt={1} px={1}>
                {canCreateField("total_quantity") && <SoftTypography variant="button" fontWeight="bold">Overall Completes: {totalCompletes.toLocaleString()}</SoftTypography>}
                {canCreateField("usd_amount") && <SoftTypography variant="button" color="info" fontWeight="bold">Total Amount: ${money(sourceAmountUsd)} USD</SoftTypography>}
              </SoftBox>
            </SoftBox>}

            {["billing_currency", "exchange_rate", "tax", "final_amount"].some(canCreateField) && <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "minmax(380px, 1fr) minmax(300px, 420px)" }} gap={3} p={1.5} sx={{ border: "1px solid #e5e7eb", borderRadius: "8px" }}>
              <SoftBox>
                <SoftTypography variant="h6" mb={1}>Currency & Tax</SoftTypography>
                <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={1.25}>
                  {canCreateField("billing_currency") && <SoftBox>
                    <SoftTypography variant="caption">Client Currency</SoftTypography>
                    <SoftSelect
                      options={currencyOptions}
                      value={currency ? (currencyOptions.find((option) => option.code === currency.code) || currency) : null}
                      placeholder="Select currency"
                      isSearchable
                      onChange={selectCurrency}
                    />
                  </SoftBox>}
                  {canCreateField("exchange_rate") && <SoftBox>
                    <SoftTypography variant="caption">Exchange Rate (1 USD)</SoftTypography>
                    <SoftInput type="text" placeholder="100.0000000" value={form.exchange_rate} inputProps={{ inputMode: "decimal" }} onChange={(event) => { const value = event.target.value; setForm((current) => ({ ...current, exchange_rate: value })); validateExchangeRate(value); }} onBlur={() => { const value = normalizeExchangeRateInput(form.exchange_rate); setForm((current) => ({ ...current, exchange_rate: value })); validateExchangeRate(value); }} />
                    {exchangeRateStatus && (
                      <SoftTypography variant="caption" display="block" sx={{ color: exchangeRateStatus.valid ? "#65b741" : "#e53935", mt: 0.35 }}>
                        {exchangeRateStatus.message}
                      </SoftTypography>
                    )}
                  </SoftBox>}
                </SoftBox>
                {canCreateField("tax") && <><FormControlLabel control={<Checkbox checked={form.tax_enabled} onChange={(event) => setForm((current) => ({ ...current, tax_enabled: event.target.checked }))} />} label="Add Tax" />
                <SoftTypography variant="caption" color={taxRule.applicable && form.tax_enabled ? "info" : "text"} display="block">
                  Tax rule: {form.tax_enabled ? (taxRule.label || "Zero Tax") : "Tax manually removed"}
                </SoftTypography></>}
              </SoftBox>
              {(canCreateField("tax") || canCreateField("final_amount")) && <SoftBox display="grid" gridTemplateColumns="1fr 1fr" gap={0.75} alignItems="center">
                {[
                  ["Amount", totals.amount], ["SGST (9%)", totals.sgst], ["CGST (9%)", totals.cgst], ["IGST (18%)", totals.igst],
                  [`Total Tax (${totals.percentage}%)`, totals.tax], ["Total", totals.total],
                ].filter(([label]) => canCreateField("tax") || ["Amount", "Total"].includes(label)).map(([label, value]) => (
                  <SoftBox key={label} display="contents"><SoftTypography variant="button" textAlign="right">{label}:</SoftTypography><SoftInput value={currencyDisplay(value, currency)} disabled /></SoftBox>
                ))}
              </SoftBox>}
            </SoftBox>}

            {canGenerateInvoice && <SoftBox mt={1.25}>
              <SoftButton
                color="info"
                sx={{ width: "100%", minHeight: "44px" }}
                disabled={saving || partyLoading || lineLoading || !company || !form.client_id || form.project_ids.length === 0 || !(Number(form.exchange_rate) > 0)}
                onClick={generateInvoice}
              >
                {saving ? "Generating..." : "Generate Invoice"}
              </SoftButton>
            </SoftBox>}

            {error && <SoftTypography color="error" variant="button" display="block" mt={1}>{error}</SoftTypography>}
          </Card>
        )}
      </SoftBox>
      <Footer />
      <SoftSnackbar color={snackbar.color} icon={snackbar.color === "success" ? "check" : "warning"} title={snackbar.color === "success" ? "Invoice Generated" : "Notice"} content={snackbar.message} open={snackbar.open} onClose={() => setSnackbar((current) => ({ ...current, open: false }))} close={() => setSnackbar((current) => ({ ...current, open: false }))} />
    </DashboardLayout>
  );
}

export default InvoiceCreate;
